import { Apis, Enums, Helpers, Store } from "utils";

export interface IPatientService {
  find(patientId?: number | null): Promise<any>;
  exist(email: string): Promise<any>;
  registerAtQiosk(username: string, email: string, phone: string): Promise<any>;
  update(data: any): Promise<any>;
  updateClinic(patientId: number, clinicId: number): Promise<any>;
  updateState(patientId: number, state: boolean): Promise<any>;
  updateValid(patientId: number, state: boolean): Promise<any>;
}

export class PatientService implements IPatientService {
  private qubehub: any;
  private controllerName: string = "patient";

  constructor(accessToken: string) {
    this.qubehub = Apis.qubehub(accessToken);
  }

  /**
   * function to get all the patients from the clinic
   */

  async find(patientId?: number | null) {
    try {
      const requestURL = patientId ? Helpers.toQueryString(this.controllerName, { patientId }) : this.controllerName;
      const [status, response] = this.#handleResponse(await this.qubehub.get(requestURL));
      return response;
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * check whether the given email exists in our database as well as returning the appointments related to it
   * @param {string} email the email to check
   */

  async exist(email: string) {
    try {
      const [status, response] = this.#handleResponse(await this.qubehub.get(Helpers.toQueryString(`${this.controllerName}/exist`, { email })));
      return response;
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * create an account with the given values
   * @param {string} username the first and last name combined
   * @param {string} email user's email
   * @param {string} phone user's phone
   */

  async registerAtQiosk(username: string, email: string, phone: string) {
    try {
      const [status, response] = this.#handleResponse(await this.qubehub.post(`${this.controllerName}/register/qiosk`, { username, email, phone: Helpers.onlyNumber(phone) }));
      return response;
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * function to update the user's infomration
   * @param {any} user user's info
   * @returns
   */

  async update(user: any) {
    try {
      delete user.firstName;
      delete user.lastName;
      return this.#handleResponse(await this.qubehub.patch(this.controllerName, user));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * function to update the patient's home clinic
   * @param {number} patientId patient ID
   * @param {number} clinicId clinic ID
   * @returns
   */

  async updateClinic(patientId: number, clinicId: number) {
    try {
      return this.#handleResponse(await this.qubehub.patch(this.controllerName, { patientId, clinicId }));
    } catch (err: any) {
      console.log(err);
    }
  }

  /**
   * function to update the user's state
   * @param {number} patientId user ID
   * @param {boolean} state whether the account should be activated or deactivated
   * @returns
   */

  async updateState(patientId: number, state: boolean) {
    try {
      return this.#handleResponse(await this.qubehub.patch(this.controllerName, { patientId, active: state }));
    } catch (err: any) {
      console.log(err);
    }
  }

  /**
   * function to update the valid state of the user
   * @param {number} patientId user ID
   * @param {boolean} state whether the profile should be set as valid or not
   * @returns
   */

  async updateValid(patientId: number, state: boolean) {
    try {
      return this.#handleResponse(await this.qubehub.patch(this.controllerName, { patientId, hcValid: state }));
    } catch (err: any) {
      console.log(err);
    }
  }

  /**
   * method to handle the response from the API
   * @param {any} response the data retrieved from the API
   * @returns
   */

  #handleResponse(response: any) {
    if (response && response.data) {
      const { status, payload } = response.data;
      if (Enums.EnumResponse.Unauthorized === status) Store.sessionStorage.clear();
      return payload ? [status, payload] : [status, null];
    }
    return [Enums.EnumResponse.None, null];
  }
}

export default PatientService;
